import { useState, useCallback, useMemo, FC } from 'react'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import ModalSkills from '../modal-skills'
import {
  ActionAddSkillCreateProcess,
  ActionAiSkillsCreateProcess,
  ActionDeleteSkillCreateProcess,
} from 'views/components/UI/BluesiteTable/Actions/GridActions'
import {
  StepsContainer,
  Row,
  SkillsSelectContainer,
  SparkleIconBlack,
  TextContainer,
  BottomTableContainer,
  BottomButtonsContainer,
  TableHeaderAbsolute,
  HeaderContainer,
  TitleContainer,
  TitleIcon,
} from '../styled'
import { H2, H3, H5, SubtitleBold } from 'views/components/UI/Text'
import Steps from 'views/components/Stepper'
import { Button, GhostButton, SecondaryButton } from 'views/components/UI/Buttons'
import Select from 'views/components/UI/Select'
import { STEPS_NEW_PROCESS } from '../../constants'

import { Skill } from 'types/common'
import { HEADERS } from '../constants'
import { Pagination, DEFAULT_LIMIT } from 'constants/global.constants'
import { useTranslation } from 'react-i18next'
import sparkle from '../../../../../../components/assets/spark_black.png'
import { AiSkillsProps, Filters } from '../skills'
import { AI_SKILLS_HEADERS, useAvailableSkills } from '../utils'
import { useSelector } from 'react-redux'
import skillsCleanIcon from 'assets/images/skills-clean-icon.png'
import { stepThreeSelector } from 'ducks/createProcess'

interface Props {
  aiSkills: AiSkillsProps[]
  setAiSkills: (aiSkills: AiSkillsProps[]) => void
  skills: Skill[]
  skillsSelected: Skill[]
  handleAddSkill: (skill: Skill) => void
  handlePagination: (page: number) => void
  pagination: Pagination
  groupOptions: Array<{ label: string; value: string }>
  setSearchFilters: (filters: Filters) => void
  searchFilters: Filters
  handleSearch: (key: string, value: string) => void
  setSkillsSelected: (skills: any[]) => void
  handleDefineMinimumRecord: (skills: any[]) => void
  handleNextStepWithMinimumRecord: (skills: any[], aiSkillsToStore?: any[]) => void
  setAiSkillsToChangeScore: (skills: any[]) => void
  setShowAiSkillsPage: (show: boolean) => void
}

const AiSkills: FC<Props> = ({
  aiSkills,
  setAiSkills,
  skills,
  skillsSelected,
  handleAddSkill,
  handlePagination,
  pagination,
  groupOptions,
  setSearchFilters,
  searchFilters,
  handleSearch,
  setSkillsSelected,
  handleDefineMinimumRecord,
  setAiSkillsToChangeScore,
  handleNextStepWithMinimumRecord,
  setShowAiSkillsPage,
}) => {
  const { t } = useTranslation()
  const stepThreeData = useSelector(stepThreeSelector)
  const [currentSkill, setCurrentSkill] = useState<any>(null)
  const [showSkillInfoModal, setShowSkillInfoModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isAiSkillsTable, setIsAiSkillsTable] = useState(true)

  const { availableSkills } = useAvailableSkills(skills, aiSkills)

  const aiSkillsModified = useMemo(() => {
    return aiSkills.map((item) => {
      return {
        ...item,
        id: item.skill_id,
        skill: item.skill,
        weight: item.ideal_profile,
        description: item.description,
      }
    })
  }, [aiSkills])

  const onDeleteAiSkill = useCallback(
    (skill: { id: number | string }) => {
      const newSkillsSelected = aiSkills.filter((item: AiSkillsProps) => item.skill_id !== skill.id)
      setAiSkills(newSkillsSelected)
    },
    [aiSkills, setAiSkills]
  )

  const onDeleteSkill = useCallback(
    (skill: { id: number | string }) => {
      const newSkillsSelected = skillsSelected.filter((item: Skill) => item.id !== skill.id)
      setSkillsSelected(newSkillsSelected)
    },
    [setSkillsSelected, skillsSelected]
  )

  const handleViewNextSkill = useCallback(() => {
    if (isAiSkillsTable) {
      const index = aiSkills.findIndex((item) => Number(item.skill_id) === Number(currentSkill.skill_id))
      if (index === -1) {
        console.error('El skill actual no se encuentra en aiSkills')
        return
      }

      const nextIndex = index === aiSkills.length - 1 ? 0 : index + 1
      const nextSkill = aiSkills[nextIndex]

      // const nextSkill = aiSkills.find((item) => Number(item.skill_id) === Number(nextSkillId))

      setCurrentSkill(nextSkill)
    } else {
      const index = availableSkills.findIndex((item) => item.id === currentSkill.id)
      if (index === availableSkills.length - 1) {
        setCurrentSkill(availableSkills[0])
      } else {
        setCurrentSkill(availableSkills[index + 1])
      }
    }
  }, [isAiSkillsTable, aiSkills, currentSkill, availableSkills])

  const handleViewPreviousSkill = useCallback(() => {
    if (isAiSkillsTable) {
      const index = aiSkills.findIndex((item) => Number(item.skill_id) === Number(currentSkill.skill_id))

      if (index === -1) {
        console.error('El skill actual no se encuentra en aiSkills')
        return
      }

      const prevIndex = index === 0 ? aiSkills.length - 1 : index - 1

      const prevSkill = aiSkills[prevIndex]

      // const prevSkill = aiSkills.find((item) => Number(item.skill_id) === Number(prevSkillId))

      setCurrentSkill(prevSkill)
    } else {
      const index = availableSkills.findIndex((item) => item.id === currentSkill.id)
      if (index === 0) {
        setCurrentSkill(availableSkills[skills.length - 1])
      } else {
        setCurrentSkill(availableSkills[index - 1])
      }
    }
  }, [isAiSkillsTable, aiSkills, skills, currentSkill, availableSkills])

  const handleCancel = () => {
    setAiSkills([])
    setShowAiSkillsPage(false)
    setSkillsSelected([])
    localStorage.removeItem('aiSkills')
    setAiSkillsToChangeScore([])
    setIsEditing(false)
  }

  const handleAddUsers = () => {
    const body = [...aiSkillsModified, ...skillsSelected]
    setSkillsSelected(body)
    setAiSkillsToChangeScore(aiSkillsModified)
    setAiSkills([])
    setShowAiSkillsPage(false)
    setIsEditing(false)
    localStorage.setItem('origin', 'only_ai_skills')
    handleNextStepWithMinimumRecord(body, aiSkillsModified)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleAddMinimumScore = () => {
    const body = [...aiSkillsModified, ...skillsSelected]
    setAiSkillsToChangeScore(aiSkillsModified)
    localStorage.setItem('aiSkills', JSON.stringify(aiSkillsModified))
    setAiSkills([])
    setShowAiSkillsPage(false)
    setIsEditing(false)
    setSkillsSelected(body)
    localStorage.setItem('origin', 'edit_ideal_profile')
    handleDefineMinimumRecord(body)
  }

  // MARK: RENDER
  return (
    <>
      <HeaderContainer>
        <TitleContainer>
          <TitleIcon src={skillsCleanIcon} />
          <H2 margin="30px 0px 24px 0px">{t('global.common.skillText')}</H2>
        </TitleContainer>
        <StepsContainer>
          <Steps steps={STEPS_NEW_PROCESS} activeStep={3} />
        </StepsContainer>
      </HeaderContainer>

      <TextContainer>
        <Row margin="0 0 20px 0">
          <H3 margin="20px 0px 0px 0px">3D Skills sugeridas con IA</H3>
          <SparkleIconBlack src={sparkle} />
        </Row>
        <H5 style={{ fontWeight: 'normal' }}>
          Este es el resultado sugerido según la información que nos compartiste. Recuerda que debes aceptar para luego
          editar o eliminar.
        </H5>
        <TableHeaderAbsolute>Puntaje</TableHeaderAbsolute>
      </TextContainer>
      <BluesiteTable
        data={aiSkillsModified}
        headers={AI_SKILLS_HEADERS}
        isAlignLeft={true}
        searchBar={false}
        showNumbers={true}
        searchLabel="Buscar competencia:"
        loading={false}
        backGroundColor="#F4F8FF"
        ActionComponent={ActionAiSkillsCreateProcess}
        extraDataAction={{
          onDeleteSkill: onDeleteAiSkill,
          onViewSkill: (e: any) => {
            setCurrentSkill(e)
            setIsAiSkillsTable(true)
            setShowSkillInfoModal(true)
          },
        }}
      />
      <BottomTableContainer>
        <GhostButton onClick={handleCancel}>Cancelar</GhostButton>
        <div>
          <SecondaryButton
            onClick={handleEdit}
            style={{
              height: '62px',
              marginRight: '16px',
            }}
          >
            Editar sugerencia
          </SecondaryButton>
          <Button onClick={handleAddUsers}>
            {stepThreeData?.use360Test ? 'Siguiente prueba' : 'Agregar usuarios'}
          </Button>
        </div>
      </BottomTableContainer>
      {currentSkill && showSkillInfoModal && (
        <ModalSkills
          data={currentSkill}
          onClose={() => setShowSkillInfoModal(false)}
          visible={showSkillInfoModal}
          onViewNextSkill={handleViewNextSkill}
          onViewPreviousSkill={handleViewPreviousSkill}
        />
      )}

      {isEditing && (
        <>
          <Row justify="space-between" margin="40px 0px 0px 0px" style={{ position: 'relative' }}>
            <SkillsSelectContainer style={{ marginBottom: '20px' }}>
              <H3>Complementa tu prueba 3D Skills</H3>
              <H5 style={{ fontWeight: 'normal', marginBottom: '40px' }}>
                Busca y agrega competencias adicionales que quieras incluir en la prueba.
              </H5>
            </SkillsSelectContainer>
            <Row style={{ position: 'absolute', left: 340, top: 103 }}>
              <SkillsSelectContainer>
                <SubtitleBold style={{ margin: '0px 16px 0px 0px' }}>Grupos de 3D Skills:</SubtitleBold>
                <Select
                  width="220px"
                  label="Grupos"
                  value={searchFilters?.groupId || ''}
                  onChange={(e) => {
                    if (e && e !== '0') {
                      handleSearch('groupId', e)
                    } else {
                      const filtersWithoutGroup = { ...searchFilters }
                      delete filtersWithoutGroup.groupId
                      setSearchFilters(filtersWithoutGroup)
                    }
                  }}
                  options={groupOptions}
                />
              </SkillsSelectContainer>
            </Row>
          </Row>
          <BluesiteTable
            data={availableSkills}
            headers={HEADERS}
            isAlignLeft={true}
            searchBar={true}
            searchLabel="Buscar competencia:"
            onSearch={(e: any) => {
              if (e !== '') {
                handleSearch('name', e)
              } else {
                const filtersWithoutName = { ...searchFilters }
                delete filtersWithoutName.name
                setSearchFilters(filtersWithoutName)
              }
            }}
            totalPages={pagination.totalPages}
            handlePagination={handlePagination}
            loading={false}
            ActionComponent={ActionAddSkillCreateProcess}
            currentPage={searchFilters.page}
            extraDataAction={{
              handleAddSkill,
              skillsSelected,
              onViewSkill: (e: any) => {
                setCurrentSkill(e)
                setIsAiSkillsTable(false)
                setShowSkillInfoModal(true)
              },
            }}
          />
          {skillsSelected.length > 0 && (
            <BluesiteTable
              data={skillsSelected}
              headers={HEADERS}
              searchBar={false}
              searchLabel="Buscar competencia:"
              totalPages={0}
              currentPage={1}
              showNumbers
              limit={DEFAULT_LIMIT}
              loading={false}
              ActionComponent={ActionDeleteSkillCreateProcess}
              extraDataAction={{
                onDeleteSkill,
              }}
            />
          )}
          <BottomButtonsContainer>
            <GhostButton onClick={handleCancel} style={{ height: '62px', margin: '0' }}>
              Cancelar
            </GhostButton>
            <Button onClick={handleAddMinimumScore}>Definir puntaje mínimo</Button>
          </BottomButtonsContainer>
        </>
      )}
    </>
  )
}

export default AiSkills
